import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';

export interface OrderItem {
  id?: number;
  productId?: number;
  productName?: string;
  productDetails?: string;
  productPrice?: number;
  quantity: number;
}

export interface Order {
  id: number;
  orderId: string;
  productDetails: string;
  productPrice: number;
  quantity: number;
  customerName: string;
  customerPhoneNumber: String;
  customerAddress: string;
  totalPrice: number;
  proofOfPaymentReceipt: string;
  paymentStatus?: string;
  items?: OrderItem[];
  createdAt?: string;
  updatedAt?: string;
}

export interface Product {
  id: number;
  businessId: string;
  sku: string;
  name: string;
  price: number;
  aliases: string[]; // Set mapped directly to string arrays
  createdAt?: string;
  updatedAt?: string;
}

export interface ProductOption {
  id: number;
  name: string;
}

export interface Expense {
  id?: number;
  businessId: string;
  itemName: string;
  amount: number;
  source?: string;
  expenseDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface MerchantProfile {
  id?: number;
  businessId: string;
  businessName: string;
  bankName: string;
  bankAccount: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubscriptionStatusResponse {
  businessId: string;
  isSubscriber: string;
  paidUntil?: string;
  totalOrders: number;
  currentFee: number;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  // private apiUrl = 'http://localhost:8080/api/dashboard';
  // private profileUrl = 'http://localhost:8080/api/merchant-profile';
  private apiUrl = 'https://notabayar-backend-production.up.railway.app/api/dashboard';
  private profileUrl = 'https://notabayar-backend-production.up.railway.app/api/merchant-profile';

  constructor(private http: HttpClient) { }

  verifyToken(token: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/verify-token`, {
      params: { token: token }
    });
  }

  submitSaleData(saleData: string, businessId: string): Observable<any> {

    const payload = {
      saleData: saleData,
      businessId: businessId
    };

    return this.http.post(
      `${this.apiUrl}/submit-sale-data`,
      payload
    );
  }

  createOrder(order: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/orders`, order);
  }

  getAllOrders(businessId: string): Observable<Order[]> {
    const params = new HttpParams().set('businessId', businessId);

    return this.http.get<Order[]>(this.apiUrl, { params: params });
  }

  updatePaymentStatus(id: string, status: string): Observable<Order> {
    return this.http.put<Order>(`${this.apiUrl}/${id}/payment-status`, { paymentStatus: status });
  }

  generateInvoice(orders: any[]): Observable<Blob> {
    // We send the list of orders to be processed into a PDF
    return this.http.post(`${this.apiUrl}/generate-invoice`, orders, {
      responseType: 'blob'
    });
  }

  generateReceipt(orders: any[]): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/generate-receipt`, orders, {
      responseType: 'blob'
    });
  }

  getProductsByBusiness(businessId: string): Observable<Product[]> {
    return this.http.get<Product[]>(`${this.apiUrl}/products/${businessId}`);
  }

  getProductOptions(businessId: string): Observable<ProductOption[]> {
    return this.http.get<ProductOption[]>(`/api/products/options/${businessId}`);
  }

  updateOrder(id: number, orderPayload: any): Observable<Order[]> {
    return this.http.put<Order[]>(`${this.apiUrl}/orders/${id}`, orderPayload);
  }

  deleteOrder(id: number): Observable<any> {
    return this.http.delete(
      `${this.apiUrl}/${id}`
    );
  }

  // Update these endpoints inside your Angular dashboard.service.ts file:

  createProduct(product: Product): Observable<Product> {
    return this.http.post<Product>(`${this.apiUrl}/products`, product);
  }

  updateProduct(id: number, product: Product): Observable<Product> {
    return this.http.put<Product>(`${this.apiUrl}/products/${id}`, product);
  }

  deleteProduct(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/products/${id}`);
  }

  // Add these inside your @Injectable DashboardService class layout:
  getExpensesByBusiness(businessId: string): Observable<Expense[]> {
    return this.http.get<Expense[]>(`${this.apiUrl}/expenses/${businessId}`);
  }

  createExpense(expense: Expense): Observable<Expense> {
    return this.http.post<Expense>(`${this.apiUrl}/expenses`, expense);
  }

  updateExpense(id: number, expense: Expense): Observable<Expense> {
    return this.http.put<Expense>(`${this.apiUrl}/expenses/${id}`, expense);
  }

  deleteExpense(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/expenses/${id}`);
  }

  getMerchantProfile(businessId: string): Observable<MerchantProfile> {
    return this.http.get<MerchantProfile>(`${this.profileUrl}/${businessId}`);
  }

  saveOrUpdateMerchantProfile(businessId: string, profile: MerchantProfile): Observable<MerchantProfile> {
    return this.http.post<MerchantProfile>(`${this.profileUrl}/${businessId}`, profile);
  }

  getSubscriptionStatus(businessId: string): Observable<SubscriptionStatusResponse> {
    return this.http.get<SubscriptionStatusResponse>(`${this.apiUrl}/${businessId}/subscription-status`);
  }
}